import {CollapsibleSection} from './CollapsibleSection';
import {SegmentGroup} from './SegmentGroup';
import {countIssues, groupBasePaths} from './lib/sections';
import type {SectionGroup} from './lib/sections';
import {hlRowTitleAndPreview} from './lib/shipmentRow';
import type {EdiTransactionExplained} from './lib/explainedEnvelope';
import type {ElementPath} from './lib/document-editor';
import type {ValidationFinding} from './lib/validator';

interface ShipmentTreeProps {
  group: SectionGroup;
  groupIndex: number;
  transactionIndex: number;
  transaction: EdiTransactionExplained;
  findings: ValidationFinding[];
  onEdit: (path: ElementPath, value: string) => void;
}

/** One row of the 856's Shipments panel — a Shipment, Order, Pack, Tare or
 * Item HL loop, shown collapsed with a one-line preview and finding-count
 * badges derived from its HL03, and recursing into its own children (Order
 * under Shipment, Pack/Tare/Item under Order) so the tree mirrors the
 * document's actual HL nesting regardless of how deep BSN05 declares it. */
export function ShipmentTree({
  group,
  groupIndex,
  transactionIndex,
  transaction,
  findings,
  onEdit,
}: ShipmentTreeProps) {
  const {title, preview} = hlRowTitleAndPreview(group);

  return (
    <CollapsibleSection
      level="row"
      title={title}
      preview={preview}
      issues={countIssues(findings, groupBasePaths([group]))}
    >
      <div className="divide-y divide-border/60">
        {group.entries.map(({segment, basePath}) => (
          <SegmentGroup
            key={basePath}
            segment={segment}
            basePath={basePath}
            groupIndex={groupIndex}
            transactionIndex={transactionIndex}
            transaction={transaction}
            findings={findings}
            onEdit={onEdit}
          />
        ))}
      </div>
      {group.subgroups && group.subgroups.length > 0 && (
        <div className="space-y-1.5 border-t border-border/60 p-1.5 pl-4">
          {group.subgroups.map(child => (
            <ShipmentTree
              key={child.key}
              group={child}
              groupIndex={groupIndex}
              transactionIndex={transactionIndex}
              transaction={transaction}
              findings={findings}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
